import React, { useState, useEffect } from 'react';
import api from '../api/client';
import { validatePhone } from '../utils/validation';
import { provisionNewAccounts } from '../utils/seedAccounts';

function GuidesManagement() {
  const [guides, setGuides] = useState([]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [role, setRole] = useState('guide');
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [provisioning, setProvisioning] = useState(false);
  const [provisionResults, setProvisionResults] = useState(null);

  useEffect(() => { loadGuides(); }, []);

  const loadGuides = async () => {
    setLoading(true);
    const data = await api.getGuides();
    if (data && data.guides) setGuides(data.guides);
    setLoading(false);
  };

  const handleAdd = async () => {
    const newErrors = {};
    const trimmedName = name.trim();

    // Validate name: 1-50 characters
    if (!trimmedName) {
      newErrors.name = 'Ce champ est obligatoire';
    } else if (trimmedName.length > 50) {
      newErrors.name = 'Maximum 50 caractères';
    }

    // Validate phone: international format
    if (!phone.trim()) {
      newErrors.phone = 'Ce champ est obligatoire';
    } else if (!validatePhone(phone)) {
      newErrors.phone = 'Format: +XXX XXXXXXX';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    const result = await api.addGuide(trimmedName, phone.trim(), role);
    if (result && result.error) {
      // Handle duplicate phone number error
      if (result.error === 'duplicate' || result.error === 'duplicate_phone' || (typeof result.error === 'string' && result.error.toLowerCase().includes('duplic'))) {
        setErrors({ phone: 'Ce numéro est déjà utilisé' });
      }
      return;
    }
    if (result && !result.error) {
      setName('');
      setPhone('');
      setRole('guide');
      setErrors({});
      loadGuides();
    }
  };

  const handleToggle = async (id) => {
    await api.toggleGuide(id);
    loadGuides();
  };

  const handleDelete = async (id, guideName) => {
    if (!window.confirm(`Supprimer "${guideName}" ? Cette action est irréversible.`)) return;
    await api.deleteGuide(id);
    loadGuides();
  };

  const handleProvision = async () => {
    setProvisioning(true);
    setProvisionResults(null);
    const results = await provisionNewAccounts(api);
    setProvisionResults(results);
    setProvisioning(false);
    loadGuides();
  };

  const admins = guides.filter(g => g.role === 'admin');
  const guidesList = guides.filter(g => g.role === 'guide');
  const poolSupervisors = guides.filter(g => g.role === 'pool_supervisor');

  return (
    <div className="animate-in">
      {/* Provision New Accounts */}
      <div style={{ background: '#1a1a1a', borderRadius: '16px', padding: '16px', marginBottom: '16px', border: '1px solid #333' }}>
        <button onClick={handleProvision} disabled={provisioning} style={{
          width: '100%', padding: '12px', background: provisioning ? '#555' : '#2563EB', color: 'white',
          border: 'none', borderRadius: '10px', fontWeight: 600, fontSize: '14px', cursor: provisioning ? 'not-allowed' : 'pointer'
        }}>
          {provisioning ? '⏳ Provisionnement en cours...' : '🚀 Provisionner les nouveaux comptes'}
        </button>
        {provisionResults && (
          <div style={{ marginTop: '12px' }}>
            {provisionResults.map((r, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', marginTop: '6px', borderRadius: '8px',
                background: r.success ? '#064E3B' : '#3B1111',
                border: `1px solid ${r.success ? '#065F46' : '#7F1D1D'}`
              }}>
                <div>
                  <span style={{ fontWeight: 500, fontSize: '13px' }}>{r.name}</span>
                  <span style={{ fontSize: '11px', color: '#888', marginLeft: '8px' }}>{r.phone}</span>
                  <span style={{ fontSize: '11px', color: '#888', marginLeft: '8px' }}>({r.role})</span>
                </div>
                <span style={{ fontSize: '12px', color: r.success ? '#6EE7B7' : '#FCA5A5' }}>
                  {r.success ? '✓' : '⚠'} {r.message}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Add Guide */}
      <div style={{ background: '#1a1a1a', borderRadius: '16px', padding: '16px', marginBottom: '16px', border: '1px solid #333' }}>
        <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '12px' }}>➕ Ajouter un guide</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div>
            <input value={name} onChange={e => { setName(e.target.value); if (errors.name) setErrors(prev => ({ ...prev, name: undefined })); }} placeholder="Nom"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', background: '#2a2a2a', border: `1px solid ${errors.name ? '#E53E3E' : '#444'}`, borderRadius: '10px', color: 'white', fontSize: '14px', outline: 'none' }} />
            {errors.name && <div style={{ color: '#FCA5A5', fontSize: '12px', marginTop: '4px' }}>{errors.name}</div>}
          </div>
          <div>
            <input value={phone} onChange={e => { setPhone(e.target.value); if (errors.phone) setErrors(prev => ({ ...prev, phone: undefined })); }} placeholder="+212 6XX XXX XXX" type="tel"
              style={{ width: '100%', boxSizing: 'border-box', padding: '10px 14px', background: '#2a2a2a', border: `1px solid ${errors.phone ? '#E53E3E' : '#444'}`, borderRadius: '10px', color: 'white', fontSize: '14px', outline: 'none' }} />
            {errors.phone && <div style={{ color: '#FCA5A5', fontSize: '12px', marginTop: '4px' }}>{errors.phone}</div>}
          </div>
          <select value={role} onChange={e => setRole(e.target.value)}
            style={{ padding: '10px 14px', background: '#2a2a2a', border: '1px solid #444', borderRadius: '10px', color: 'white', fontSize: '14px', outline: 'none' }}>
            <option value="guide">Guide</option>
            <option value="pool_supervisor">Superviseur Piscine</option>
            <option value="admin">Admin</option>
          </select>
          <button onClick={handleAdd} style={{
            padding: '12px', background: '#E53E3E', color: 'white', border: 'none',
            borderRadius: '10px', fontWeight: 600, fontSize: '14px', cursor: 'pointer'
          }}>Ajouter</button>
        </div>
      </div>

      {/* Admins */}
      <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '8px', color: '#F59E0B' }}>
        👑 Administrateurs ({admins.length})
      </div>
      {admins.map(g => (
        <GuideCard key={g.id} guide={g} onToggle={handleToggle} onDelete={handleDelete} />
      ))}

      {/* Pool Supervisors */}
      {poolSupervisors.length > 0 && (
        <>
          <div style={{ fontWeight: 700, fontSize: '14px', margin: '16px 0 8px', color: '#38BDF8' }}>
            🏊 Superviseurs Piscine ({poolSupervisors.length})
          </div>
          {poolSupervisors.map(g => (
            <GuideCard key={g.id} guide={g} onToggle={handleToggle} onDelete={handleDelete} />
          ))}
        </>
      )}

      {/* Guides */}
      <div style={{ fontWeight: 700, fontSize: '14px', margin: '16px 0 8px', color: '#10B981' }}>
        🧭 Guides ({guidesList.length})
      </div>
      {loading ? (
        <div style={{ textAlign: 'center', padding: '20px' }}><div className="spinner" style={{ margin: '0 auto' }}></div></div>
      ) : (
        guidesList.map(g => (
          <GuideCard key={g.id} guide={g} onToggle={handleToggle} onDelete={handleDelete} />
        ))
      )}
    </div>
  );
}

function GuideCard({ guide, onToggle, onDelete }) {
  return (
    <div style={{
      background: '#1a1a1a', borderRadius: '12px', padding: '12px 14px',
      marginBottom: '6px', border: '1px solid #333',
      opacity: guide.is_active ? 1 : 0.5,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between'
    }}>
      <div>
        <div style={{ fontWeight: 500, fontSize: '14px' }}>{guide.name}</div>
        <div style={{ fontSize: '11px', color: '#888' }}>{guide.phone}</div>
      </div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <a href={`https://wa.me/${guide.phone?.replace(/[^0-9]/g,'')}`} target="_blank" rel="noreferrer" style={{
          background: '#25D366', border: 'none', color: 'white', borderRadius: '8px',
          padding: '6px 10px', fontSize: '12px', textDecoration: 'none', display: 'flex', alignItems: 'center'
        }}>💬</a>
        <button onClick={() => onToggle(guide.id)} style={{
          background: guide.is_active ? '#2a2a2a' : '#064E3B', border: '1px solid #444',
          color: guide.is_active ? '#F59E0B' : '#6EE7B7', borderRadius: '8px',
          padding: '6px 10px', fontSize: '12px', cursor: 'pointer'
        }}>
          {guide.is_active ? '🚫' : '✓'}
        </button>
        <button onClick={() => onDelete(guide.id, guide.name)} style={{
          background: '#3B1111', border: '1px solid #7F1D1D', color: '#FCA5A5',
          borderRadius: '8px', padding: '6px 10px', fontSize: '12px', cursor: 'pointer'
        }}>🗑</button>
      </div>
    </div>
  );
}

export default GuidesManagement;
